import { useRef, useState, useReducer } from "react";
import { useEffect } from "react";
import { request } from "service/request";

export const useCaptureClickOutside = () => {
  const [clickOutside, setClickOutside] = useState(false);
  const ref = useRef(null);

  const handleClickOutside = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setClickOutside(true);
    } else {
      setClickOutside(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [ref]);

  return { ref, clickOutside, setClickOutside };
};

export function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    // Add event listener
    window.addEventListener("resize", handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
}

export function useFetch(url) {
  const cache = useRef({});

  const cancelRequest = useRef(false);

  const initialState = {
    error: undefined,
    data: undefined,
  };

  const fetchReducer = (state, action) => {
    switch (action.type) {
      case "loading":
        return { ...initialState };
      case "fetched":
        return { ...initialState, data: action.payload };
      case "error":
        return { ...initialState, error: action.payload };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(fetchReducer, initialState);

  useEffect(() => {
    if (!url) return;

    cancelRequest.current = false;

    const fetchData = async () => {
      dispatch({ type: "loading" });

      if (cache.current[url]) {
        dispatch({ type: "fetched", payload: cache.current[url] });
        return;
      }

      try {
        const response = await request({
          url: url,
        });
        if (!response.success) {
          throw new Error(response.success);
        }
        const data = await response;
        cache.current[url] = data;
        if (cancelRequest.current) return;
      } catch (error) {
        if (cancelRequest.current) return;
        dispatch({ type: "error", payload: error });
      }
    };
    fetchData();

    return () => {
      cancelRequest.current = true;
    };
  }, [url]);

  return state;
}

export const useDevicePixelRatio = () => {
  const dpr = window.devicePixelRatio;
  const [currentDpr, setCurrentDpr] = useState(dpr);

  useEffect(() => {
    const canListen = typeof window !== "undefined" && "matchMedia" in window;
    if (!canListen) {
      return;
    }
    const updateDpr = () => setCurrentDpr(window.devicePixelRatio);

    const mediaMatcher = window.matchMedia(`screen and (resolution: ${currentDpr}dppx)`);
    if (mediaMatcher.addEventListener) {
      mediaMatcher.addEventListener("change", updateDpr);
    } else {
      mediaMatcher.addListener(updateDpr);
    }

    return () => {
      if (mediaMatcher.removeEventListener) {
        mediaMatcher.removeEventListener("change", updateDpr);
      } else {
        mediaMatcher.removeListener(updateDpr);
      }
    };
  }, [currentDpr]);

  return currentDpr;
};

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value; //assign the value of ref to the argument
  }, [value]); //this code will run when the value of 'value' changes
  return ref.current; //in the end, return the current ref value.
}
